import { useMemo } from 'react';
import type { GetTransactionsParams } from '@/lib/api/transactions';
import { mapTransactionApiToUi } from '@/lib/mappers/transactions';
import { generateMockTransactions } from '@/lib/mock-transactions';
import type { PaymentStatusChange } from '@/types/api/payment-status';
import type {
  CustomerTransactionsApiResponse,
  CustomerTransactionsSummaryApi,
  GetCustomerTransactionsParams,
  TransactionApi,
  TransactionDashboardApi,
  TransactionsApiResponse,
  TransactionsPaginationApi,
  TransactionsSummaryApi,
} from '@/types/api/transactions';
import type { Transaction } from '@/types/transaction';
import { useApi } from './useApi';
import { useModeAwareQuery } from './useModeAwareQuery';
import { usePaymentStatusChanges } from './usePaymentStatusChanges';

/**
 * Enrich transactions with payment link status from external API.
 * Matches transaction referenceNumber to externalReference in the status changes.
 */
function mergePaymentStatuses(
  transactions: Transaction[],
  statusMap: Map<string, PaymentStatusChange> | undefined,
): Transaction[] {
  if (!statusMap || statusMap.size === 0) return transactions;
  return transactions.map((t) => {
    const change = statusMap.get(t.referenceNumber);
    if (change) {
      return { ...t, paymentLinkStatus: change.status };
    }
    return t;
  });
}

/**
 * Hook to fetch transactions list data
 * Handles both Demo and Live modes
 */
export function useTransactionsList(params?: GetTransactionsParams) {
  const { apiGet } = useApi();
  const mockTransactions = generateMockTransactions();

  return useModeAwareQuery<Transaction[], Error>(
    ['transactions', params],
    async ({ signal }) => {
      const response = await apiGet<TransactionsApiResponse>('/api/v1/transactions', params, signal);
      return response.data.map(mapTransactionApiToUi);
    },
    mockTransactions,
    {
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );
}

/**
 * Hook to fetch a single transaction by ID (includes line items).
 * Enriches the result with payment link status from the external API.
 */
export function useTransactionDetail(transactionId: number | null) {
  const { apiGet } = useApi();
  const mockTransactions = generateMockTransactions();
  const mockTransaction = transactionId ? (mockTransactions.find((x) => x.id === transactionId) ?? null) : null;

  const detailQuery = useModeAwareQuery<Transaction | null, Error>(
    ['transaction-detail', transactionId],
    async ({ signal }) => {
      if (transactionId === null) return null;
      const response = await apiGet<TransactionApi>(`/api/v1/transactions/${transactionId}`, undefined, signal);
      return mapTransactionApiToUi(response);
    },
    mockTransaction,
    {
      enabled: transactionId !== null,
      staleTime: 0,
    },
  );

  const { data: statusMap } = usePaymentStatusChanges();

  const enrichedData = useMemo(() => {
    const transaction = detailQuery.data;
    if (!transaction || !statusMap) return transaction ?? null;
    const change = statusMap.get(transaction.referenceNumber);
    if (change) {
      return { ...transaction, paymentLinkStatus: change.status };
    }
    return transaction;
  }, [detailQuery.data, statusMap]);

  return {
    ...detailQuery,
    data: enrichedData,
  };
}

/**
 * Combined hook for transactions page data
 * Returns transactions list enriched with payment link statuses
 */
export function useTransactionsPageData(params?: GetTransactionsParams) {
  const transactionsQuery = useTransactionsList(params);
  const { data: statusMap } = usePaymentStatusChanges();

  const transactions = useMemo(
    () => mergePaymentStatuses(transactionsQuery.data ?? [], statusMap),
    [transactionsQuery.data, statusMap],
  );

  return {
    transactions,
    isLoading: transactionsQuery.isLoading,
    error: transactionsQuery.error,
    refetch: () => {
      transactionsQuery.refetch();
    },
  };
}

/**
 * Result type for main transactions page with server-side pagination
 */
export interface TransactionsPaginatedResult {
  transactions: Transaction[];
  pagination: TransactionsPaginationApi | null;
  summary: TransactionsSummaryApi | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch transactions with server-side pagination, filtering, and sorting.
 * Follows the same pattern as useCustomerTransactionsPaginated.
 * Enriches transactions with payment link status from external API.
 */
export function useTransactionsPaginated(params?: GetTransactionsParams): TransactionsPaginatedResult {
  const { apiGet } = useApi();
  const mockTransactions = generateMockTransactions();
  const { data: statusMap } = usePaymentStatusChanges();

  const query = useModeAwareQuery<TransactionsApiResponse, Error>(
    ['transactions-paginated', params],
    async ({ signal }) => {
      return apiGet<TransactionsApiResponse>('/api/v1/transactions', params, signal);
    },
    {
      data: mockTransactions.map((x) => x as unknown as TransactionApi),
      pagination: {
        page: 1,
        pageSize: 25,
        totalItems: mockTransactions.length,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
      },
      summary: { totalTransactions: mockTransactions.length },
    },
    {
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );

  const transactions = useMemo(
    () => mergePaymentStatuses((query.data?.data ?? []).map(mapTransactionApiToUi), statusMap),
    [query.data, statusMap],
  );

  return {
    transactions,
    pagination: query.data?.pagination ?? null,
    summary: query.data?.summary ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: () => {
      query.refetch();
    },
  };
}

/**
 * Hook to fetch dashboard statistics for metric cards.
 * Returns global metrics (not affected by table filters).
 */
export function useTransactionsDashboard() {
  const { apiGet } = useApi();

  return useModeAwareQuery<TransactionDashboardApi, Error>(
    ['transactions-dashboard'],
    async ({ signal }) => {
      return apiGet<TransactionDashboardApi>('/api/v1/transactions/dashboard', undefined, signal);
    },
    {
      totalTransactions: 0,
      totalOutstanding: 0,
      overdueAmount: 0,
      atRiskAmount: 0,
      unallocatedReceiptsCount: 0,
      unallocatedReceiptsAmount: 0,
      invoiceCount: 0,
      receiptCount: 0,
      creditNoteCount: 0,
    },
    {
      staleTime: 30000,
      refetchOnMount: 'always',
    },
  );
}

/**
 * Result type for customer transaction hooks with server-side pagination
 */
export interface CustomerTransactionsResult {
  transactions: Transaction[];
  pagination: TransactionsPaginationApi | null;
  summary: CustomerTransactionsSummaryApi | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

/**
 * Hook to fetch transactions for a specific customer using the dedicated
 * server-side paginated endpoint: GET /api/v1/customers/{customerId}/transactions
 *
 * Supports pagination, sorting, filtering via query params.
 * Use outstandingStatus to filter by outstanding vs completed transactions.
 */
export function useCustomerTransactionsPaginated(
  customerId: string | undefined,
  params?: GetCustomerTransactionsParams,
): CustomerTransactionsResult {
  const { apiGet } = useApi();
  const mockTransactions = generateMockTransactions();
  const outstandingStatus = params?.outstandingStatus;

  const mockFiltered = useMemo(() => {
    const customerIdNum = customerId ? parseInt(customerId, 10) : undefined;
    return mockTransactions.filter((t) => {
      if (customerIdNum !== undefined && t.customerId !== customerIdNum) return false;
      if (outstandingStatus === 'outstanding') return (t.outstandingAmount ?? 0) !== 0;
      if (outstandingStatus === 'completed') return (t.outstandingAmount ?? 0) === 0;
      return true;
    });
  }, [customerId, mockTransactions, outstandingStatus]);

  const query = useModeAwareQuery<CustomerTransactionsApiResponse, Error>(
    ['customer-transactions', customerId, params],
    async ({ signal }) => {
      if (!customerId) {
        return {
          data: [],
          pagination: {
            page: 1,
            pageSize: 25,
            totalItems: 0,
            totalPages: 0,
            hasNextPage: false,
            hasPreviousPage: false,
          },
          summary: {
            totalTransactions: 0,
            totalOutstanding: 0,
            totalOverdue: 0,
            totalOutstandingCustomer: 0,
            totalOverdueCustomer: 0,
          },
        };
      }
      return apiGet<CustomerTransactionsApiResponse>(`/api/v1/customers/${customerId}/transactions`, params, signal);
    },
    {
      data: mockFiltered.map((t) => t as unknown as TransactionApi),
      pagination: {
        page: 1,
        pageSize: 25,
        totalItems: mockFiltered.length,
        totalPages: 1,
        hasNextPage: false,
        hasPreviousPage: false,
      },
      summary: {
        totalTransactions: mockFiltered.length,
        totalOutstanding: 0,
        totalOverdue: 0,
        totalOutstandingCustomer: 0,
        totalOverdueCustomer: 0,
      },
    },
    {
      enabled: !!customerId,
      staleTime: 0,
      refetchOnMount: 'always',
    },
  );

  const transactions = useMemo(() => (query.data?.data ?? []).map(mapTransactionApiToUi), [query.data]);

  return {
    transactions,
    pagination: query.data?.pagination ?? null,
    summary: query.data?.summary ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: () => {
      query.refetch();
    },
  };
}

/**
 * Hook to fetch outstanding transactions for a specific customer.
 * Uses the dedicated server-side paginated endpoint with outstandingStatus=outstanding.
 * Backwards-compatible return shape for existing consumers.
 */
export function useCustomerTransactions(
  customerId: string | undefined,
  params?: Omit<GetCustomerTransactionsParams, 'outstandingStatus'>,
) {
  const result = useCustomerTransactionsPaginated(customerId, {
    ...params,
    outstandingStatus: 'outstanding',
  });

  return {
    transactions: result.transactions,
    pagination: result.pagination,
    summary: result.summary,
    isLoading: result.isLoading,
    error: result.error,
    refetch: result.refetch,
  };
}

/**
 * Hook to fetch completed transactions for a specific customer.
 * Uses the dedicated server-side paginated endpoint with outstandingStatus=completed.
 * Backwards-compatible return shape for existing consumers.
 */
export function useCustomerCompletedTransactions(
  customerId: string | undefined,
  params?: Omit<GetCustomerTransactionsParams, 'outstandingStatus'>,
) {
  const result = useCustomerTransactionsPaginated(customerId, {
    ...params,
    outstandingStatus: 'completed',
  });

  return {
    transactions: result.transactions,
    pagination: result.pagination,
    summary: result.summary,
    isLoading: result.isLoading,
    error: result.error,
    refetch: result.refetch,
  };
}
